import { fetchStaticLayout } from '@/actions/ecom/layout';
import PageLayout from '@/components/Layouts/PageLayout';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import RecentlyViewed from '@/components/Home/RecentlyViewed';
import HeroBannerCarousel from '@/components/Home/HeroBannerCarousel';
import HelpDevice from '@/components/Home/HelpDevice';
import SingleImagesBanner from '@/shared/crisp/SingleImagesBanner';
import HomeProductBanners from '@/components/Home/HomeProductBanners';
import NewLaunches from '@/components/Home/NewLaunches';
import AMCServiceFilters from '@/components/Home/AMCServiceFilters';
import WhyBuy from '@/components/Home/WhyBuy';
import AppDownloadCard from '@/components/Static/AppDownloadCard';
import { MemoExoticComponent } from 'react';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import { NextPageContext } from 'next';
import React from 'react';
import Cookies from '@/utils/cookie';
import { EFL_RECENTLY_VIEWED_PRODUCTS, RECENTLY_VIEWED_PRODUCTS_COUNT } from '@/constants/app';
import { getProductsDetail } from '@/actions/ecom/productsDetail';
import dlv from 'dlv';
import { MapType } from '@/types';
import dynamic from 'next/dynamic';
import withLazyLoad from '@/hoc/withLazyLoad';
import { CMS_COMPONENTS } from '@/constants/cms';
import ProductBannerExtraLarge from '@/shared/crisp/ProductBannerExtraLarge';
import DynamicPlaceholder from '@/shared/crisp/DynamicPlaceholder';
import { useAppConfigContext } from '@/context/AppContext';
import Categories from '@/components/Home/Categories';

const TrendingProducts = dynamic(
  () => import(/* webpackChunkName: "home-lazy-chunks-" */ '@/components/Home/TrendingProducts'),
  {
    ssr: false,
  },
);

const TabularBanner = dynamic(
  () => import(/* webpackChunkName: "home-lazy-chunks-" */ '@/shared/crisp/TabularBanner'),
  {
    ssr: false,
  },
);

const ProductDemo = dynamic(
  () => import(/* webpackChunkName: "home-lazy-chunks-" */ '@/components/Product/ProductDemo'),
  {
    ssr: false,
  },
);

const STATIC_MAP: { [key: string]: MemoExoticComponent<any> | any } = {
  [CMS_COMPONENTS.PRODUCT_BANNERS.EXTRA_LARGE]: ProductBannerExtraLarge,
  [CMS_COMPONENTS.SHARED.RECENTLY_VIEWED]: RecentlyViewed,
  [CMS_COMPONENTS.BANNERS.MEDIA_CAROUSEL]: HeroBannerCarousel,
  [CMS_COMPONENTS.SHARED.NEED_HELP_WITH_DEVICE]: HelpDevice,
  [CMS_COMPONENTS.BANNERS.SINGE_IMAGE]: SingleImagesBanner,
  [CMS_COMPONENTS.PRODUCT_BANNERS.LARGE]: HomeProductBanners,
  [CMS_COMPONENTS.SHARED.NEW_LAUNCHES]: NewLaunches,
  [CMS_COMPONENTS.AMC.SERVICE_AND_FILTERS]: AMCServiceFilters,
  [CMS_COMPONENTS.SHARED.WHY_BUY_FROM_US]: WhyBuy,
  [CMS_COMPONENTS.SHARED.TRENDING_PRODUCTS]: withLazyLoad(TrendingProducts),
  [CMS_COMPONENTS.BANNERS.TABULAR_TABS]: withLazyLoad(TabularBanner),
  [CMS_COMPONENTS.SHARED.APP_DOWNLOAD_CARD]: AppDownloadCard,
  [CMS_COMPONENTS.SHARED.EXPERT_GUIDANCE_DEMO]: ProductDemo,
  [CMS_COMPONENTS.SHARED.DYNAMIC_PLACEHOLDER]: DynamicPlaceholder,
  [CMS_COMPONENTS.SHARED.CATEGORIES]: Categories,
};

// export async function getServerSideProps() {
// export async function getStaticProps(context: NextPageContext) {
//   const layout = await fetchStaticLayout('home-page');
//   return {
//     props: {
//       layout,
//     },
//     revalidate: 60,
//   };
// }

export async function getServerSideProps(nextPageContext: NextPageContext) {
  const { req } = nextPageContext;
  const serverCookie = req?.headers?.cookie || '';

  const promises = [fetchStaticLayout('home-page')];
  // GET Product Details for Recently Viewed Products
  let recentlyViewedIds = Cookies.get(EFL_RECENTLY_VIEWED_PRODUCTS, serverCookie);
  recentlyViewedIds = recentlyViewedIds
    ? recentlyViewedIds.split(',').slice(0, RECENTLY_VIEWED_PRODUCTS_COUNT).join(',')
    : recentlyViewedIds;

  promises.push(
    recentlyViewedIds
      ? getProductsDetail({ ids: recentlyViewedIds, populate: 'media' })
      : Promise.resolve([]),
  );

  try {
    let [page, recentlyViewed]: any[] = await Promise.allSettled(promises);

    if (page.status === 'fulfilled') {
      page = page.value;
    }
    if (recentlyViewed.status === 'fulfilled') {
      recentlyViewed = recentlyViewed.value;
    } else {
      recentlyViewed = [];
    }

    const sharedAppDownloadComp = page.layout.find(
      (item: any) => item.__component == CMS_COMPONENTS.SHARED.APP_DOWNLOAD_CARD,
    );
    const componentProps = {
      [CMS_COMPONENTS.SHARED.RECENTLY_VIEWED]: {
        list:
          recentlyViewed?.map(({ attributes }: { attributes: any }) => {
            const { plainName, slug, materialId, media } = attributes;
            return {
              plainName,
              slug,
              materialId,
              image: dlv(media, 'data.0.attributes.formats.thumbnail.url', null),
            };
          }) || [],
      },
      [CMS_COMPONENTS.SHARED.APP_DOWNLOAD_CARD]: {
        ...sharedAppDownloadComp,
        background: 'bg-cs-secondary',
      },
    };

    return {
      props: {
        page,
        componentProps,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, nextPageContext);
  }
}

export default function HomePage({ page, componentProps }: { page: any; componentProps: MapType }) {
  const layout = useLayoutRenderer(page.layout, STATIC_MAP, componentProps);
  const { state: appConfig } = useAppConfigContext();
  return (
    <PageLayout
      showTopMenu={!Boolean(appConfig.isFlutterClient)}
      seo={page.seo}
      topPadding={Boolean(appConfig.isFlutterClient)}
    >
      {layout}
    </PageLayout>
  );
}
